import React, { useState } from "react";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaPlusCircle,
  FaExclamationTriangle,
} from "react-icons/fa";

const MedicationTracker = ({ treatmentID }) => {
  const [medications, setMedications] = useState([
    {
      id: 1,
      name: "Paracetamol",
      genericName: "Acetaminophen",
      dose: "500mg",
      unit: "mg",
      schedule: [
        { time: "15:00PM", status: null, timestamp: "", date: "2024-11-29" },
        { time: "16:00PM", status: null, timestamp: "", date: "2024-11-29" },
      ],
      repeatTill: "2024-12-01T23:59:59Z",
      procedure: "Chemotherapy",
    },
    {
      id: 1,
      name: "Paracetamol",
      genericName: "Acetaminophen",
      dose: "500mg",
      unit: "mg",
      schedule: [
        { time: "15:00PM", status: null, timestamp: "", date: "2024-11-29" },
        { time: "16:00PM", status: null, timestamp: "", date: "2024-11-29" },
      ],
      repeatTill: "2024-12-01T23:59:59Z",
      procedure: "Chemotherapy",
    },
  ]);

  const [showEmergencyForm, setShowEmergencyForm] = useState(false);
  const [emergencyMedication, setEmergencyMedication] = useState({
    name: "",
    genericName: "",
    dose: "",
    unit: "",
  });

  const todayDate = new Date().toISOString().split("T")[0]; // Format: YYYY-MM-DD

  const handleAddEmergencyMedication = () => {
    if (
      emergencyMedication.name &&
      emergencyMedication.genericName &&
      emergencyMedication.dose &&
      emergencyMedication.unit
    ) {
      setMedications([
        {
          id: Date.now(),
          ...emergencyMedication,
          procedure: "Emergency",
          repeatTill: new Date().toISOString(),
          schedule: [
            {
              time: new Date().toLocaleTimeString(),
              status: "given",
              timestamp: new Date().toLocaleTimeString(),
              date: todayDate,
            },
          ],
        },
        ...medications,
      ]);
      setEmergencyMedication({
        name: "",
        genericName: "",
        dose: "",
        unit: "",
      });
      setShowEmergencyForm(false);
    }
  };

  const handleMarkAsGiven = (medId, scheduleIndex) => {
    const now = new Date().toLocaleTimeString();
    setMedications((prevMedications) =>
      prevMedications.map((med) =>
        med.id === medId
          ? {
              ...med,
              schedule: med.schedule.map((dose, index) =>
                index === scheduleIndex
                  ? { ...dose, status: "given", timestamp: now }
                  : dose
              ),
            }
          : med
      )
    );
  };

  const handleMarkAsMissed = (medId, scheduleIndex) => {
    const now = new Date().toLocaleTimeString();
    setMedications((prevMedications) =>
      prevMedications.map((med) =>
        med.id === medId
          ? {
              ...med,
              schedule: med.schedule.map((dose, index) =>
                index === scheduleIndex
                  ? { ...dose, status: "missed", timestamp: now }
                  : dose
              ),
            }
          : med
      )
    );
  };

  return (
    <div className="p-4 rounded-xl">
      <div className="flex justify-between items-center mb-6">
        {/* Title */}
        <h3 className="text-xl font-semibold text-gray-800">
          Medication Tracker
        </h3>

        {/* Button */}
        {!showEmergencyForm && (
          <button
            onClick={() => setShowEmergencyForm(true)}
            className="flex items-center space-x-3 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white font-medium text-lg rounded-full shadow-lg hover:shadow-xl hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-green-300"
          >
            <FaPlusCircle className="w-6 h-6" />
            <span>Add Medication</span>
          </button>
        )}
      </div>

      {/* Emergency Medication Form */}
      {showEmergencyForm && (
        <div className="relative mb-6 p-8 bg-white shadow-md rounded-lg border-t-4 border-red-500">
          {/* Close Button */}
          <button
            onClick={() => setShowEmergencyForm(false)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none"
            aria-label="Close Emergency Form"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <h4 className="text-lg font-semibold text-gray-700 flex items-center space-x-2 mb-6">
            <FaExclamationTriangle className="text-red-500" />
            <span>Emergency Medication</span>
          </h4>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Medicine Name */}
            <div className="space-y-2">
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700"
              >
                Medicine Name
              </label>
              <input
                id="name"
                type="text"
                value={emergencyMedication.name}
                onChange={(e) =>
                  setEmergencyMedication({
                    ...emergencyMedication,
                    name: e.target.value,
                  })
                }
                placeholder="Enter medicine name"
                className="block w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            {/* Generic Name */}
            <div className="space-y-2">
              <label
                htmlFor="genericName"
                className="block text-sm font-medium text-gray-700"
              >
                Generic Name
              </label>
              <input
                id="genericName"
                type="text"
                value={emergencyMedication.genericName}
                onChange={(e) =>
                  setEmergencyMedication({
                    ...emergencyMedication,
                    genericName: e.target.value,
                  })
                }
                placeholder="Enter generic name"
                className="block w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            {/* Dose */}
            <div className="space-y-2">
              <label
                htmlFor="dose"
                className="block text-sm font-medium text-gray-700"
              >
                Dose (e.g., 500mg)
              </label>
              <input
                id="dose"
                type="text"
                value={emergencyMedication.dose}
                onChange={(e) =>
                  setEmergencyMedication({
                    ...emergencyMedication,
                    dose: e.target.value,
                  })
                }
                placeholder="Enter dose"
                className="block w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            {/* Unit */}
            <div className="space-y-2">
              <label
                htmlFor="unit"
                className="block text-sm font-medium text-gray-700"
              >
                Unit (e.g., mg, ml)
              </label>
              <input
                id="unit"
                type="text"
                value={emergencyMedication.unit}
                onChange={(e) =>
                  setEmergencyMedication({
                    ...emergencyMedication,
                    unit: e.target.value,
                  })
                }
                placeholder="Enter unit"
                className="block w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleAddEmergencyMedication}
              className="px-8 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 shadow-md transition-all transform hover:scale-105"
            >
              Save Emergency Medication
            </button>
          </div>
        </div>
      )}

      {/* Medication List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {medications.map((med) => (
          <div
            key={med.id}
            className="p-6 bg-white border rounded-lg shadow-md hover:shadow-lg transition-shadow space-y-4"
          >
            {/* Medication Header */}
            <div className="space-y-2">
              <h4 className="text-lg font-bold text-gray-800">{med.name}</h4>
              <p className="text-sm text-gray-500 italic">{med.genericName}</p>
            </div>

            {/* Medication Details */}
            <div className="space-y-1">
              <p className="text-gray-600">
                <strong>Dose:</strong> {med.dose} {med.unit}
              </p>
              <p className="text-gray-600">
                <strong>Procedure:</strong> {med.procedure}
              </p>
              <p className="text-gray-600">
                <strong>Repeat Till:</strong>{" "}
                {new Date(med.repeatTill).toLocaleDateString()}
              </p>
            </div>

            {/* Divider */}
            <hr className="border-gray-300" />

            {/* Schedule */}
            <div className="space-y-3">
              <h5 className="text-md font-semibold text-gray-700">
                Schedule for Today:
              </h5>
              {med.schedule
                .filter((dose) => dose.date === todayDate)
                .map((dose, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      dose.status === "given"
                        ? "bg-green-50"
                        : dose.status === "missed"
                          ? "bg-red-50"
                          : "bg-gray-100"
                    }`}
                  >
                    <div>
                      <p className="text-gray-700">
                        <strong>Time:</strong> {dose.time}
                      </p>
                      {dose.timestamp && (
                        <p className="text-xs text-gray-500">
                          <strong>Status:</strong>{" "}
                          <span
                            className={`${
                              dose.status === "given"
                                ? "text-green-500"
                                : "text-red-500"
                            }`}
                          >
                            {dose.status === "given" ? "Given" : "Missed"}
                          </span>{" "}
                          at {dose.timestamp}
                        </p>
                      )}
                    </div>
                    {!dose.status && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleMarkAsGiven(med.id, index)}
                          className="text-green-500 hover:text-green-700"
                        >
                          <FaCheckCircle size={20} />
                        </button>
                        <button
                          onClick={() => handleMarkAsMissed(med.id, index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FaTimesCircle size={20} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MedicationTracker;
